import {
  sendAnalyticsInfo,
  sendAnalyticsError,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import type { AppDispatch } from 'features/app/store/store';

export interface ConnectionStartAnalyticsPayload {
  sessionId: string;
  instanceNumber: number;
  totalConnections: number;
  activeConnections: number;
}

export interface ConnectionCloseAnalyticsPayload {
  instanceNumber: number;
  totalConnections: number;
  activeConnections: number;
}

export interface WebSocketOpenAnalyticsPayload {
  sessionId: string;
  wsUrl: string;
  eventType: string;
  eventTimestamp: number;
}

export interface WebSocketErrorAnalyticsPayload {
  sessionId: string;
  wsUrl: string;
  readyState: number;
  eventType: string;
  eventTimestamp: number;
}

export interface WebSocketCloseAnalyticsPayload {
  sessionId: string;
  wsUrl: string | undefined;
  code: number;
  reason: string;
  wasClean: boolean;
  eventType: string;
  eventTimestamp: number;
  readyState: number | undefined;
}

export function dispatchConnectionStartAnalytics(
  dispatch: AppDispatch,
  payload: ConnectionStartAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'TrainingConnectionSavvy.startConnection',
      eventName: 'training_forker_connection_start',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export function dispatchConnectionCloseAnalytics(
  dispatch: AppDispatch,
  payload: ConnectionCloseAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'TrainingConnectionSavvy.closeConnection',
      eventName: 'training_forker_connection_close',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export function dispatchWebSocketOpenAnalytics(
  dispatch: AppDispatch,
  payload: WebSocketOpenAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'TrainingConnectionSavvy.WebSocketOpen',
      eventName: 'training_forker_ws_open',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export function dispatchWebSocketErrorAnalytics(
  dispatch: AppDispatch,
  payload: WebSocketErrorAnalyticsPayload
) {
  dispatch(
    sendAnalyticsError({
      Method: 'TrainingConnectionSavvy.WebSocketError',
      eventName: 'training_forker_ws_error',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export function dispatchWebSocketCloseAnalytics(
  dispatch: AppDispatch,
  payload: WebSocketCloseAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'TrainingConnectionSavvy.WebSocketClose',
      eventName: 'training_forker_ws_close',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export interface RetryAttemptAnalyticsPayload {
  sessionId: string;
  attempt: number;
  maxAttempts: number;
  delayMs: number;
  wsUrl: string;
}

export interface RetryExhaustedAnalyticsPayload {
  sessionId: string;
  totalAttempts: number;
  wsUrl: string;
}

export function dispatchRetryAttemptAnalytics(
  dispatch: AppDispatch,
  payload: RetryAttemptAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'TrainingConnectionSavvy.RetryAttempt',
      eventName: 'training_forker_ws_retry_attempt',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export function dispatchRetryExhaustedAnalytics(
  dispatch: AppDispatch,
  payload: RetryExhaustedAnalyticsPayload
) {
  dispatch(
    sendAnalyticsError({
      Method: 'TrainingConnectionSavvy.RetryExhausted',
      eventName: 'training_forker_ws_retry_exhausted',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export interface QualityDegradedAnalyticsPayload {
  reason: string;
  peerId: string;
  stream?: 'received' | 'sent';
  videoBitrate?: number;
  videoJitter?: number;
  receivedVideoJitter?: number;
  receivedVideoPacketLossRate?: number;
  videoFramesPerSecond?: number;
  videoRoundTripTime?: number;
  sentVideoJitter?: number;
  sentVideoPacketLossRate?: number;
  qualityLimitationReason?: string;
  videoFreezeCount?: number;
  videoTotalFreezesDuration?: number;
}

/** Emits `training_forker_quality_degraded` when the gate latches on a bad signal. */
export function dispatchQualityDegradedAnalytics(
  dispatch: AppDispatch,
  payload: QualityDegradedAnalyticsPayload
) {
  dispatch(
    sendAnalyticsWarning({
      Method: 'useTrainingForkerQualityMonitor.qualityDegradedDetected',
      eventName: 'training_forker_quality_degraded',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export interface QualityHealthAnalyticsPayload {
  peerId: string;
  qualityDegraded: boolean;
  qualityDegradedReason?: string | null;
  videoBitrate?: number;
  videoFramesPerSecond?: number;
  videoFreezeCount?: number;
  videoTotalFreezesDuration?: number;
  receivedVideoPacketLossRate?: number;
  receivedVideoJitter?: number;
  sentVideoPacketLossRate?: number;
  sentVideoJitter?: number;
  qualityLimitationReason?: string;
}

/** Periodic `training_forker_quality_health` heartbeat while the monitor is active. */
export function dispatchQualityHealthAnalytics(
  dispatch: AppDispatch,
  payload: QualityHealthAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'useTrainingForkerQualityMonitor.qualityHealthHeartbeat',
      eventName: 'training_forker_quality_health',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}

export interface ForkStoppedQualityAnalyticsPayload {
  reason: string;
  sipCallId?: string;
  callId?: number;
}

export function dispatchForkStoppedQualityAnalytics(
  dispatch: AppDispatch,
  payload: ForkStoppedQualityAnalyticsPayload
) {
  dispatch(
    sendAnalyticsWarning({
      Method: 'useTrainingForker.stopForkingDueToQuality',
      eventName: 'training_forker_fork_stopped_quality',
      details: payload,
      logGroup: 'training_forker',
      // State CallId is already 0 if this is emitted during call teardown.
      ...(typeof payload.callId === 'number' && payload.callId > 0
        ? { CallId: payload.callId }
        : {}),
    })
  );
}

export interface CaptureCallSummaryAnalyticsPayload {
  CallId?: number;
  SipCallId?: string;
  SavvySessionId?: string;
  /** Sum of policy-eligible forking intervals (selectIsForkingAllowed). */
  EligibleDurationSec: number;
  CapturedDurationSec: number;
  /** CapturedDurationSec / EligibleDurationSec * 100, capped at 100, 2 decimals. */
  CapturePct: number;
  WasQualityTerminated: boolean;
}

export function dispatchCaptureCallSummaryAnalytics(
  dispatch: AppDispatch,
  payload: CaptureCallSummaryAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: 'useTrainingForkerCaptureSummary.captureCallSummary',
      eventName: 'training_forker_capture_call_summary',
      logGroup: 'training_forker',
      // State CallId and SipCallId are already cleared when this fires.
      ...(typeof payload.CallId === 'number' && payload.CallId > 0
        ? { CallId: payload.CallId }
        : {}),
      ...(payload.SipCallId ? { SipCallId: payload.SipCallId } : {}),
      ...(payload.SavvySessionId
        ? { SavvySessionId: payload.SavvySessionId }
        : {}),
      // Top-level numerics so DataSet can avg without JSON parse.
      CapturePct: payload.CapturePct,
      EligibleDurationSec: payload.EligibleDurationSec,
      CapturedDurationSec: payload.CapturedDurationSec,
      WasQualityTerminated: payload.WasQualityTerminated,
    })
  );
}

export type TeardownStep =
  | 'cancel_retry'
  | 'close_peer_connection'
  | 'close_websocket'
  | 'dispose_senders'
  | 'reset_retry_state'
  | 'complete';

export interface TeardownStepAnalyticsPayload {
  step: TeardownStep;
  sessionId?: string;
  timestampUtc: string;
  peerConnectionState?: string;
  socketReadyState?: number;
}

export function dispatchTeardownStepAnalytics(
  dispatch: AppDispatch,
  payload: TeardownStepAnalyticsPayload
) {
  dispatch(
    sendAnalyticsInfo({
      Method: `TrainingConnectionSavvy.closeConnection.${payload.step}`,
      eventName: 'training_forker_teardown_step',
      details: payload,
      logGroup: 'training_forker',
    })
  );
}
